import { useCallback, useEffect, useState } from "react";
import { useTabParam } from "@/lib/useTabParam";
import { toast } from "sonner";
import { Repeat } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label, cad, errMsg } from "@/components/sponsored/format";
import { AdminScMerchants } from "@/components/admin/sponsored/AdminScMerchants";
import { AdminScRefunds } from "@/components/admin/sponsored/AdminScRefunds";
import { AdminFeeReviews } from "@/components/admin/sponsored/AdminFeeReviews";

const TABS = ["merchants", "refunds", "fees"];

function Totals({ d }) {
  const t = d.totals;
  const tiles = [["Funded by merchants", t.funded_cad, "funded"], ["Available", t.available_cad, "available"], ["Set aside", t.reserved_cad, "reserved"],
    ["Pending rewards", t.pending_cad, "pending"], ["Paid to shoppers", t.settled_cad, "settled"], ["Refund hold", t.refund_hold_cad, "refund-hold"],
    ["Refunded to cards", t.refunded_cad, "refunded"]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-sc-totals">
      {tiles.map(([k, v, id]) => (
        <div key={id} className="pf-card p-4 min-w-0" data-testid={`admin-sc-total-${id}`}><Label>{k}</Label><div className="font-display text-xl xl:text-2xl font-medium tabular-nums mt-2 truncate" title={cad(v)} data-testid={`admin-sc-total-${id}-value`}>{cad(v)}</div></div>
      ))}
    </div>
  );
}

export default function AdminSponsoredCashback() {
  const { user } = useAuth();
  const sa = user?.role === "super_admin";
  const [tab, setTab] = useTabParam("merchants", TABS);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/sponsored-cashback/overview").then((r) => setD(r.data)).catch(() => setD({ error: true })), []);
  useEffect(() => { load(); }, [load]);
  const runMaintenance = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/sponsored-cashback/run-maintenance"); toast.success(`Upkeep done: ${r.data.released} released, ${r.data.expired} expired, ${r.data.settled} paid out`); load(); }
    catch (e) { toast.error(errMsg(e, "Upkeep failed")); } finally { setBusy(false); }
  };
  if (!d) return <p className="text-sm text-slate-500" data-testid="admin-sc-loading">Loading…</p>;
  if (d.error) return <p className="text-sm text-rose-600" data-testid="admin-sc-error">Couldn't load Sponsored Cashback. <button onClick={() => { setD(null); load(); }} className="underline font-semibold" data-testid="admin-sc-retry">Try again</button></p>;
  const f = d.flags;
  return (
    <div className="space-y-6" data-testid="admin-sponsored-cashback">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex flex-wrap gap-2">
            <span className="pf-chip pf-chip-mint"><Repeat className="w-3 h-3" /> Sponsored Cashback</span>
            <span className="pf-chip" data-testid="admin-sc-flags">Merchant {f.merchant ? "on" : "off"} · Shopper {f.shopper ? "on" : "off"}{f.production_blocked ? " · production blocked" : ""}</span>
          </div>
          <h2 className="font-display text-3xl font-medium tracking-tight mt-3 text-slate-900">Returning-shopper cashback</h2>
          <p className="text-sm text-slate-600 mt-1">Cyannect base cashback: {d.base_rate_display}. {d.eligibilities_total} eligibilities · {d.rewards_total} rewards.</p>
        </div>
        {sa && <button onClick={runMaintenance} disabled={busy} className="pf-btn-glass px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-50" data-testid="admin-sc-run-maintenance">{busy ? "Running…" : "Run upkeep now"}</button>}
      </div>
      <Totals d={d} />
      <Tabs value={tab} onValueChange={setTab} className="pf-card p-6">
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="merchants" data-testid="admin-sc-tab-merchants">Merchants</TabsTrigger>
          <TabsTrigger value="refunds" data-testid="admin-sc-tab-refunds">Refund requests{d.open_refund_requests ? ` (${d.open_refund_requests})` : ""}</TabsTrigger>
          <TabsTrigger value="fees" data-testid="admin-sc-tab-fees">Fee reviews</TabsTrigger>
        </TabsList>
        <TabsContent value="merchants" className="pt-4"><AdminScMerchants rows={d.merchants} /></TabsContent>
        <TabsContent value="refunds" className="pt-4"><AdminScRefunds sa={sa} onChange={load} /></TabsContent>
        <TabsContent value="fees" className="pt-4"><AdminFeeReviews sa={sa} /></TabsContent>
      </Tabs>
    </div>
  );
}
